/**
 * Samouczek „Pierwsze kroki” (prośba właściciela 2026-10-13): konfiguracja konta firmy w kilku krokach, każdy
 * z podpowiedzią, gdzie to jest w serwisie, i z możliwością pominięcia kroku albo całości.
 *
 * Tu same dane i czyste funkcje (testy w `kroki.test.js`). Stan kroków liczymy z danych konta (profil, klienci,
 * zespół, preferencje, wycena), więc krok zrobiony poza samouczkiem też jest odhaczony. W pamięci przeglądarki
 * (`pamiec.js`) zostaje tylko to, czego z danych nie widać: pominięte kroki, potwierdzenia, odwiedzony kalendarz.
 *
 * `fakty` (składa `context/SamouczekContext.jsx`):
 *   firmowe      konto firmowe (`account_type === 'company'`)
 *   firma        podpięta firma (NIP)
 *   rola         'owner' | 'member' | null
 *   uprawnienia  klucze uprawnień w zespole
 *   limit        limit kont w firmie (domyślnie 5)
 *   logo         jest logo firmy
 *   klientow     liczba klientów bez ofert bez klienta (null = nie wiadomo)
 *   wlasneTerminy jest termin w kalendarzu dodany ręcznie (nie z importu)
 *   osob, zaproszen  osoby w zespole i ważne zaproszenia
 *   ankieta      firma zapisała własną ankietę na Rynku (profil domyślny się nie liczy)
 *   preferencje  coś wybrane w preferencjach doboru
 *   cennikPodpis skrót marż, stawek montażu i usług (wykrywa zmianę cennika)
 */

export const WERSJA = 1

const mozeFirma = (f) => !f.firma || f.rola === 'owner'
const maUprawnienie = (f, klucz) => !f.firma || f.rola === 'owner' || (f.uprawnienia || []).includes(klucz)

export const KROKI = [
  {
    klucz: 'firma',
    tytul: 'Twoja firma na ofertach',
    krotko: 'Logo i kolor firmy na ofertach',
    minut: 0.5,
    gdzie: ['Menu konta', 'Ustawienia', 'Firma'],
    adres: () => '/konto?sekcja=firma',
    dla: mozeFirma,
    opis: (f) => (f.firma
      ? 'Dodaj logo i wybierz kolor. Pojawią się na ofertach, projektach i w pasku u góry.'
      : 'Podaj NIP, a dane firmy pobierzemy z Białej listy VAT. Konto stanie się firmowe: dostaniesz pulpit, klientów, kalendarz i zespół.'),
    zadania: (f) => [
      { tekst: 'Dane firmy z NIP', zrobione: Boolean(f.firma) },
      { tekst: 'Logo i kolor firmy', zrobione: Boolean(f.firma && f.logo) },
    ],
    cel: (f) => (f.firma ? 'logo' : 'firma-nip'),
    dymek: (f) => (f.firma
      ? { tytul: 'Tu dodasz logo', tekst: 'PNG, JPG albo WEBP. Kolor wybierzesz tuż pod spodem, a na końcu kliknij Zapisz wygląd.' }
      : { tytul: 'Zacznij od NIP', tekst: 'Wpisz NIP i kliknij Pobierz dane. Nazwę i adres uzupełnimy z rejestru, a Ty klikasz Powiąż konto z firmą.' }),
    zrobiony: (f) => Boolean(f.firma && f.logo),
    gotowe: 'Logo i kolor są już na ofertach i w pasku u góry.',
  },
  {
    // Krok dodany 2026-11-02: samouczek powstał przed Rynkiem i nie mówił o tym, po co wiele firm zakłada konto.
    klucz: 'rynek',
    tytul: 'Zlecenia od klientów',
    krotko: 'Ankieta firmy i wizytówka',
    minut: 1.5,
    gdzie: ['Rynek ofert', 'Wizytówka'],
    adres: () => '/firma/ankieta',
    dla: mozeFirma,
    opis: () => 'Zaproszenia do zleceń już do Ciebie trafiają, domyślnie z całej Polski. W ankiecie zaznacz swój teren, marki i usługi, a w wizytówce dodaj zdjęcia i oceny Google. Ofertę składasz w 48 godzin, a kontakt dostajesz, gdy klient wybierze Twoją firmę.',
    cel: () => 'ankieta',
    dymek: () => ({ tytul: 'Ankieta firmy', tekst: 'Sprawdź teren i zakres, dodaj marki i usługi, a na dole kliknij Zapisz ankietę. Pod ankietą jest wizytówka dla klientów.' }),
    zrobiony: (f) => Boolean(f.ankieta),
    gotowe: 'Ankieta zapisana. Zaproszenia dopasujemy do Twojego terenu, marek i usług.',
  },
  {
    klucz: 'klienci',
    tytul: 'Twoi klienci w jednym miejscu',
    krotko: 'Import klientów z Excela',
    minut: 1,
    gdzie: ['Menu konta', 'Klienci', 'Importuj'],
    adres: () => '/klienci',
    dla: () => true,
    opis: () => 'Wgraj klientów z pliku Excel albo CSV, także tych po montażu. Pobierz wzór, wklej swoje dane i sprawdź podgląd przed zapisem.',
    cel: () => 'import',
    dymek: () => ({ tytul: 'Tu zaimportujesz klientów', tekst: 'Wybierz plik, a my dopasujemy kolumny i etapy. Wolisz ręcznie? Obok jest Nowy klient.' }),
    zrobiony: (f) => (f.klientow || 0) > 0,
    gotowe: 'Klienci są w panelu. Na pulpicie zobaczysz ich w kalendarzu, na mapie montaży i w kolejnych krokach.',
  },
  {
    klucz: 'zespol',
    tytul: 'Zaproś swój zespół',
    krotko: 'Konta dla handlowców i ekip',
    minut: 0.5,
    gdzie: ['Menu konta', 'Zespół'],
    adres: () => '/konto?sekcja=zespol',
    dla: (f) => maUprawnienie(f, 'zespol'),
    opis: (f) => {
      const limit = f.limit || 5
      return `Na koncie firmy pracuje do ${limit} osób: Ty i ${limit - 1} zaproszone. Każdej nadasz uprawnienia, np. widok wszystkich klientów albo zmianę cennika. Pracujesz sam? Pomiń ten krok.`
    },
    cel: () => 'zapros',
    dymek: () => ({ tytul: 'Zaproś pierwszą osobę', tekst: 'Wpisz jej adres e-mail i zaznacz, co może robić. Link w mailu jest ważny 7 dni.' }),
    zrobiony: (f) => (f.osob || 0) > 1 || (f.zaproszen || 0) > 0,
    gotowe: 'Zaproszenie poszło. Gdy ktoś je przyjmie, zobaczysz go w zakładce Zespół i w filtrze kalendarza.',
  },
  {
    klucz: 'kalendarz',
    tytul: 'Kalendarz spotkań i montaży',
    krotko: 'Spotkania, montaże i serwis',
    minut: 0.5,
    gdzie: ['Menu konta', 'Kalendarz'],
    adres: () => '/klienci?widok=kalendarz',
    dla: () => true,
    opis: () => 'Planuj spotkania, montaże i serwis. Każdy termin ma klienta i osobę z zespołu, a filtr Konta pokaże kalendarz jednego doradcy albo jednej ekipy.',
    cel: () => 'kalendarz',
    dymek: () => ({ tytul: 'Tu dodasz termin', tekst: 'Kliknij Nowy termin, wybierz klienta, rodzaj i osobę. Terminy z kart klientów pojawiają się tu same.' }),
    zrobiony: (f, p) => (p.odwiedzone || []).includes('kalendarz') || Boolean(f.wlasneTerminy),
    gotowe: 'Wiesz już, gdzie planować. Terminy z kart klientów trafiają do kalendarza same.',
  },
  {
    klucz: 'cennik',
    tytul: 'Twój cennik',
    krotko: 'Marże, montaż i usługi',
    minut: 1.5,
    gdzie: ['Menu konta', 'Ustawienia', 'Marże'],
    adres: () => '/konto?sekcja=marze',
    dla: (f) => maUprawnienie(f, 'wycena'),
    opis: () => 'Ustaw marże, stawki montażu i usługi dodatkowe. Asystent doboru i oferty policzą wtedy cenę dla klienta według Twojego cennika.',
    zadania: () => [
      { tekst: 'Marże', adres: '/konto?sekcja=marze' },
      { tekst: 'Stawki montażu', adres: '/konto?sekcja=montaz' },
      { tekst: 'Usługi dodatkowe', adres: '/konto?sekcja=uslugi' },
    ],
    potwierdzenie: 'Mam ustawione',
    cel: () => 'marze',
    dymek: () => ({ tytul: 'Marże według mocy instalacji', tekst: 'Stawki montażu i usługi dodatkowe są w zakładkach obok. Na dole każdej zakładki kliknij Zapisz zmiany.' }),
    zrobiony: (f, p) => (p.potwierdzone || []).includes('cennik')
      || Boolean(p.cennikStart && f.cennikPodpis && f.cennikPodpis !== p.cennikStart),
    gotowe: 'Cena dla klienta liczy się teraz według Twojego cennika.',
  },
  {
    klucz: 'sprzet',
    tytul: 'Sprzęt, z którym pracujesz',
    krotko: 'Marki i serie w doborze',
    minut: 1,
    gdzie: ['Menu konta', 'Preferencje doboru'],
    adres: () => '/preferencje',
    dla: () => true,
    opis: () => 'Wybierz producentów, serie i modele modułów, falowników i magazynów. Asystent doboru i Projekt 3D zaproponują je jako pierwsze.',
    cel: () => 'preferencje',
    dymek: () => ({ tytul: 'Twoje marki i serie', tekst: 'Zaznacz producentów i serie, z których składasz zestawy, a na dole strony kliknij Zapisz preferencje.' }),
    zrobiony: (f) => Boolean(f.preferencje),
    gotowe: 'Asystent doboru i Projekt 3D zaproponują najpierw Twój sprzęt.',
  },
]

/** Czas kroku do pokazania: „30 s”, „1 min”, „1,5 min” (właściciel 2026-10-13: całość ma się mieścić w ok. 5 minutach). */
export const czasKroku = (minut) => (minut < 1 ? `${Math.round(minut * 60)} s` : `${String(minut).replace('.', ',')} min`)
/** Czas całości zaokrąglony w górę do pełnych minut. */
export const czasRazem = (minut) => `${Math.max(1, Math.ceil(minut))} min`

export const krokPoKluczu = (klucz) => KROKI.find((k) => k.klucz === klucz) || null

/** Kroki, które to konto może zrobić (pracownik bez uprawnień nie dostaje cennika ani zespołu). */
export const krokiDlaKonta = (fakty) => KROKI.filter((k) => k.dla(fakty || {}))

/** Dlaczego krok jeszcze czeka (albo null): konto bez firmy nie ma panelu klientów, kalendarz otwiera pierwszy klient. */
export const powodCzekania = (krok, f) => {
  if (krok.klucz === 'firma') return null
  if (!f.firmowe) return 'Po podaniu NIP firmy w pierwszym kroku'
  if (['zespol', 'rynek'].includes(krok.klucz) && !f.firma) return 'Po podaniu NIP firmy w pierwszym kroku'
  if (krok.klucz === 'kalendarz' && f.klientow === 0) return 'Kalendarz otworzy się po dodaniu pierwszego klienta'
  return null
}

/** 'zrobiony' | 'pominiety' | 'czeka' | 'otwarty'. Zrobiony wygrywa z pominiętym: krok zrobiony później się odhacza. */
export const stanKroku = (krok, fakty, pamiec) => {
  const f = fakty || {}
  const p = pamiec || {}
  if (krok.zrobiony(f, p)) return 'zrobiony'
  if ((p.pominiete || []).includes(krok.klucz)) return 'pominiety'
  if (powodCzekania(krok, f)) return 'czeka'
  return 'otwarty'
}

/**
 * Pełny stan samouczka: lista kroków z ich stanem, bieżący krok (wybrany albo pierwszy otwarty), postęp i koniec.
 * Wybrany krok zostaje bieżącym także po zrobieniu: panel pokazuje wtedy „Zrobione” i przycisk „Dalej”.
 */
export const stanSamouczka = (fakty, pamiec) => {
  const p = pamiec || {}
  const kroki = krokiDlaKonta(fakty).map((k) => ({ krok: k, stan: stanKroku(k, fakty, p) }))
  const wybrany = kroki.find((k) => k.krok.klucz === p.krok)
  const nastepny = kroki.find((k) => k.stan === 'otwarty') || kroki.find((k) => k.stan === 'czeka') || null
  const biezacy = wybrany || nastepny
  const zrobione = kroki.filter((k) => k.stan === 'zrobiony').length
  const pominiete = kroki.filter((k) => k.stan === 'pominiety').length
  const koniec = kroki.length > 0 && kroki.every((k) => k.stan === 'zrobiony' || k.stan === 'pominiety')
  return {
    kroki,
    biezacy: koniec && (!wybrany || wybrany.stan !== 'zrobiony') ? null : biezacy,
    zrobione,
    pominiete,
    wszystkich: kroki.length,
    koniec,
    minut: kroki.filter((k) => k.stan !== 'zrobiony').reduce((s, k) => s + k.krok.minut, 0),
  }
}

/** Następny krok do zrobienia po danym (w kolejności listy, potem od początku), z pominięciem zrobionych i pominiętych. */
export const krokPo = (klucz, fakty, pamiec) => {
  const { kroki } = stanSamouczka(fakty, pamiec)
  const i = kroki.findIndex((k) => k.krok.klucz === klucz)
  const kolejne = [...kroki.slice(i + 1), ...kroki.slice(0, Math.max(0, i))]
  const cel = kolejne.find((k) => k.stan === 'otwarty') || kolejne.find((k) => k.stan === 'czeka')
  return cel ? cel.krok.klucz : null
}

/** Skrót cennika do wykrycia zmiany (bez logo i koloru, bo te są w kroku „Twoja firma”). */
export const podpisCennika = (w) => (w ? JSON.stringify([
  w.margin_mode, w.margin_ranges, w.install_ranges, w.storage_install_ranges, w.storage_margin_ranges,
  w.extra_services, w.vat_rate, w.only_final_price,
]) : null)

/** Czy w preferencjach doboru coś jest wybrane (producenci, serie albo modele). */
export const preferencjeUstawione = (p) => {
  if (!p) return false
  const listy = ['inverter_manufacturers', 'battery_manufacturers', 'module_manufacturers']
  if (listy.some((k) => (p[k] || []).length > 0)) return true
  return Object.values(p.wybrane || {}).some((kat) => Object.values(kat || {}).some((x) => Array.isArray(x) && x.length > 0))
}

/** Termin dodany ręcznie (nie z importu klientów, który zapisuje montaż jako `import-montaz`). */
export const maWlasneTerminy = (klienci, terminyKlienta) => (klienci || []).some((k) => (terminyKlienta(k) || [])
  .some((t) => t && t.od && !String(t.id || '').startsWith('import')))
